import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";

/** Campo con desplegable de valores posibles (ATC, agrupación homogénea, PA, laboratorio,
 *  conjunto SPR) que se abre al entrar en el campo, sin tener que escribir nada (feedback de
 *  Carlos, 20 oct 2026: «los usuarios no se saben los códigos de memoria»). Sin texto, la API
 *  devuelve los grupos ATC de primer nivel o los valores del universo de la organización; al
 *  escribir, filtra (en ATC, baja de nivel: C → C01…C10). El `<datalist>` de antes solo
 *  enseñaba opciones a partir de la segunda letra. */
type Item = { value: string; label: string; count: number };

export default function SuggestInput({ kind, value, onChange, placeholder, style, ariaLabel }: {
  kind: string; value: string; onChange: (v: string) => void; placeholder?: string;
  style?: React.CSSProperties; ariaLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Item[]>([]);
  const [active, setActive] = useState(-1);
  const seq = useRef(0);
  const box = useRef<HTMLSpanElement>(null);
  const [alignRight, setAlignRight] = useState(false);
  const enabled = ["atc", "ah", "pa", "lab", "spr"].includes(kind);

  useEffect(() => {
    if (!open || !enabled) return;
    const n = ++seq.current;
    const h = setTimeout(() => {
      api.get<{ items: Item[] }>(`/portfolio/scopes/suggest?kind=${kind}&q=${encodeURIComponent(value.trim())}&limit=40`)
        .then(r => { if (n === seq.current) { setItems(r.items); setActive(-1); } })
        .catch(() => { if (n === seq.current) setItems([]); });
    }, value.trim() ? 250 : 0);
    return () => clearTimeout(h);
  }, [open, kind, value, enabled]);

  const choose = (v: string) => { onChange(v); setOpen(kind === "atc" && v.length < 7); };

  return (
    <span ref={box} style={{ position: "relative", display: "inline-block", ...(style?.width ? { width: style.width } : {}) }}>
      <input value={value} placeholder={placeholder} aria-label={ariaLabel ?? placeholder}
        role="combobox" aria-expanded={open && items.length > 0} aria-autocomplete="list"
        onChange={e => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => {
          const r = box.current?.getBoundingClientRect();
          setAlignRight(!!r && r.left + 460 > window.innerWidth - 16);
          setOpen(true);
        }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={e => {
          if (e.key === "Escape") setOpen(false);
          else if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setActive(a => Math.min(a + 1, items.length - 1)); }
          else if (e.key === "ArrowUp") { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
          else if (e.key === "Enter" && open && active >= 0 && items[active]) { e.preventDefault(); choose(items[active].value); }
        }}
        style={{ ...style, width: "100%" }} />
      {open && enabled && items.length > 0 && (
        <span role="listbox" style={{
          position: "absolute", top: "calc(100% + 4px)", ...(alignRight ? { right: 0 } : { left: 0 }), zIndex: 400,
          minWidth: "100%", width: "max-content", maxWidth: "min(460px, calc(100vw - 32px))", maxHeight: 300, overflowY: "auto", background: "var(--clr-modal-solid)",
          border: "1px solid var(--clr-border)", borderRadius: 12, boxShadow: "0 12px 32px -12px rgba(0,0,0,0.35)", padding: 4,
        }}>
          {items.map((it, i) => (
            <span key={it.value} role="option" aria-selected={i === active}
              onMouseDown={e => { e.preventDefault(); choose(it.value); }}
              onMouseEnter={() => setActive(i)}
              style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "6px 10px", borderRadius: 8, cursor: "pointer",
                       fontSize: "0.8rem", color: "var(--clr-text)", background: i === active ? "var(--clr-hover)" : "transparent" }}>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.label}</span>
              <span style={{ color: "var(--clr-text4)", fontSize: "0.72rem", flexShrink: 0 }}>{it.count}</span>
            </span>
          ))}
        </span>
      )}
    </span>
  );
}
